import type React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import type { CompetencyQuestion, CompetencyQuestionTemplate } from "../../../../pages/jobs/types";

interface CompetencyTemplatePreviewProps {
  template: CompetencyQuestionTemplate | null;
  open: boolean;
  onClose: () => void;
}

const CompetencyTemplatePreview: React.FC<CompetencyTemplatePreviewProps> = ({
  template,
  open,
  onClose,
}) => {
  const theme = useSelector((state: RootState) => state.theme);

  if (!template) return null;

  const totalPoints = template.questions.reduce(
    (sum: number, q: CompetencyQuestion) => sum + q.points,
    0
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: `1px solid ${theme.primary_colors[800]}` }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ color: theme.primary_colors[100] }}>
            Test Preview: {template.name}
          </Typography>
          <Chip
            label={template.department}
            size="small"
            sx={{
              backgroundColor: theme.primary_colors[400],
              color: theme.primary_colors[900],
              fontWeight: 600,
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Test Summary */}
          <Paper
            sx={{
              p: 2,
              backgroundColor: theme.primary_colors[900],
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 2,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                sx={{ color: theme.primary_colors[300], mb: 0.5 }}
              >
                Total Questions
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: theme.primary_colors[100],
                  fontWeight: 700,
                }}
              >
                {template.questions.length}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                sx={{ color: theme.primary_colors[300], mb: 0.5 }}
              >
                Maximum Score
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: theme.primary_colors[100],
                  fontWeight: 700,
                }}
              >
                {totalPoints} pts
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                sx={{ color: theme.primary_colors[300], mb: 0.5 }}
              >
                Question Types
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.primary_colors[200],
                }}
              >
                {template.questions.filter(
                  (q: CompetencyQuestion) => q.type === "single_choice"
                ).length}{" "}
                MC
                {", "}
                {template.questions.filter(
                  (q: CompetencyQuestion) => q.type === "open_text"
                ).length}{" "}
                Text
              </Typography>
            </Box>
          </Paper>

          {/* Questions Preview */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: theme.primary_colors[100], fontWeight: 600 }}
            >
              Questions
            </Typography>

            {template.questions.map((question: CompetencyQuestion, idx: number) => (
              <Card
                key={question.id}
                sx={{
                  backgroundColor: theme.primary_colors[900],
                  boxShadow: 0,
                  border: `1px solid ${theme.primary_colors[800]}`,
                }}
              >
                <CardContent>
                  {/* Question Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.primary_colors[300],
                        fontWeight: 600,
                      }}
                    >
                      Question {idx + 1} (
                      {question.type === "single_choice"
                        ? "Multiple Choice"
                        : "Open Text"}
                      )
                    </Typography>
                    <Chip
                      label={`${question.points} pts`}
                      size="small"
                      sx={{
                        backgroundColor: theme.primary_colors[400],
                        color: theme.primary_colors[900],
                        height: 24,
                      }}
                    />
                  </Box>

                  {/* Question Text */}
                  <Typography
                    sx={{
                      color: theme.primary_colors[100],
                      mb: 1.5,
                      fontWeight: 500,
                    }}
                  >
                    {question.question}
                  </Typography>

                  {/* Options for Single Choice */}
                  {question.type === "single_choice" && (
                    <Box sx={{ mt: 1.5 }}>
                      <RadioGroup>
                        {(question.options || []).map((option, optIdx) => (
                          <FormControlLabel
                            key={optIdx}
                            value={optIdx}
                            control={
                              <Radio
                                disabled
                                sx={{
                                  color: theme.primary_colors[400],
                                  "&.Mui-checked": {
                                    color: theme.primary_colors[400],
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography
                                sx={{
                                  color: theme.primary_colors[200],
                                  fontSize: "0.875rem",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {option}
                                {question.correctOptionIndex === optIdx && (
                                  <Chip
                                    label="Correct"
                                    size="small"
                                    sx={{
                                      backgroundColor: "rgba(76, 175, 80, 0.2)",
                                      color: "#4CAF50",
                                      height: 20,
                                      fontSize: "0.65rem",
                                    }}
                                  />
                                )}
                              </Typography>
                            }
                            sx={{
                              mb: 1,
                              ".MuiFormControlLabel-label": {
                                width: "100%",
                              },
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </Box>
                  )}

                  {/* Open Text Preview */}
                  {question.type === "open_text" && (
                    <Paper
                      sx={{
                        p: 1.5,
                        backgroundColor: theme.primary_colors[900],
                        border: `1px dashed ${theme.primary_colors[700]}`,
                        minHeight: 80,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.primary_colors[300],
                          fontStyle: "italic",
                          fontSize: "0.875rem",
                        }}
                      >
                        [Candidates enter text answer here]
                      </Typography>
                    </Paper>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Footer Info */}
          <Paper
            sx={{
              p: 2,
              backgroundColor: theme.primary_colors[900],
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: theme.primary_colors[300] }}
            >
              This preview shows how the competency test will appear to candidates.
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.primary_colors[400],
                fontWeight: 600,
              }}
            >
              Total: {totalPoints} points
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CompetencyTemplatePreview;
